<template>
  <section class="tw:flex tw:max-w-[620px] tw:flex-col tw:gap-7" data-testid="reg-step-profile">
    <RegistrationStepHeading :title="$t('registration.profile.title')" :lede="$t('registration.profile.lede')" />

    <div class="tw:flex tw:flex-col tw:gap-[18px]">
      <div class="tw:grid tw:grid-cols-2 tw:gap-4">
        <LeadInput
          v-model="firstName"
          type="text"
          autocomplete="given-name"
          :label="$t('registration.profile.first_name')"
          :errors="fieldErrors.firstName"
          required
          data-testid="reg-first-name" />

        <LeadInput
          v-model="lastName"
          type="text"
          autocomplete="family-name"
          :label="$t('registration.profile.last_name')"
          :errors="fieldErrors.lastName"
          required
          data-testid="reg-last-name" />
      </div>

      <div class="tw:grid tw:grid-cols-2 tw:gap-4">
        <LeadInput
          v-model="jobTitle"
          type="text"
          autocomplete="organization-title"
          :label="$t('registration.profile.job_title')"
          data-testid="reg-job-title">
          <template #iconStart><IconBriefcase :size="16" /></template>
        </LeadInput>

        <!-- same value as the header selector; the platform persists the language onto the account on every request -->
        <div class="tw:flex tw:flex-col tw:gap-1.5">
          <label class="tw:text-14 tw:font-medium tw:text-gray-700" for="reg-language">
            {{ $t('registration.profile.language') }}
          </label>
          <LanguageSelect id="reg-language" class="tw:w-full" data-testid="reg-profile-language" />
        </div>
      </div>
    </div>

    <!-- mobile confirmation is required before the step can be submitted -->
    <div
      class="tw:flex tw:flex-col tw:gap-4 tw:rounded-lg tw:border tw:border-border tw:bg-gray-50 tw:p-6"
      data-testid="reg-mobile-card">
      <div class="tw:flex tw:items-start tw:justify-between tw:gap-6">
        <div class="tw:flex tw:min-w-0 tw:flex-col tw:gap-1">
          <span class="tw:text-14 tw:font-semibold tw:text-gray-900">{{
            $t('registration.profile.mobile_title')
          }}</span>

          <span class="tw:text-13 tw:text-gray-600" data-testid="reg-mobile-number">
            <template v-if="changingNumber">
              <span class="tw:flex tw:flex-col tw:gap-2">
                <LeadInput
                  v-model="correctedMobile"
                  type="tel"
                  autocomplete="tel"
                  :label="$t('registration.profile.mobile_correct')"
                  :errors="fieldErrors.mobile"
                  data-testid="reg-mobile-correct" />
                <span class="tw:flex tw:items-center tw:gap-3">
                  <LeadButton
                    variant="primary"
                    :loading="isSendingCode"
                    data-testid="reg-mobile-save"
                    @click="saveNumber">
                    {{ $t('registration.profile.mobile_save') }}
                  </LeadButton>
                  <LeadButton
                    variant="link"
                    :disabled="isSendingCode"
                    data-testid="reg-mobile-cancel"
                    @click="cancelChange">
                    {{ $t('registration.profile.mobile_cancel') }}
                  </LeadButton>
                </span>
              </span>
            </template>

            <template v-else>
              {{ $t('registration.profile.mobile_sent_to') }}
              <strong class="tw:font-medium">{{ displayNumber }}</strong>
              <span class="tw:mx-1 tw:text-muted">&middot;</span>
              <button
                type="button"
                class="tw:font-medium tw:text-primary tw:hover:underline"
                data-testid="reg-mobile-change"
                @click="startChangingNumber">
                {{ $t('registration.profile.mobile_change') }}
              </button>
            </template>
          </span>
        </div>

        <LeadPill :tone="mobileVerified ? 'success' : 'neutral'" role="status" data-testid="reg-mobile-status">
          <IconCheck v-if="mobileVerified" :size="14" />
          {{ mobileVerified ? $t('registration.profile.mobile_verified') : $t('registration.profile.mobile_pending') }}
        </LeadPill>
      </div>

      <template v-if="!mobileVerified && !changingNumber">
        <!-- the boxes stay visible; only the action text changes from "Send" to "Resend" -->
        <CodeInput
          ref="codeRef"
          v-model="code"
          size="md"
          :show-paste-hint="false"
          :has-error="Boolean(error)"
          :disabled="isVerifying"
          :aria-label="$t('registration.profile.code_label')"
          data-testid="reg-mobile-code"
          @complete="verify" />

        <span v-if="error" class="tw:text-13 tw:text-danger" role="alert" data-testid="reg-mobile-error">
          {{ error }}
        </span>

        <span class="tw:text-13 tw:text-muted">
          <!-- before a request, both actions are offered -->
          <template v-if="!codeSent">
            <button
              type="button"
              class="tw:font-medium tw:text-primary tw:hover:underline tw:disabled:cursor-not-allowed tw:disabled:text-muted"
              :disabled="isSendingCode || isCalling"
              data-testid="reg-mobile-send"
              @click="() => sendCode()">
              {{ isSendingCode ? $t('registration.profile.mobile_sending') : $t('registration.profile.mobile_send') }}
            </button>
            {{ ' ' }}{{ $t('registration.profile.mobile_or') }}{{ ' ' }}
            <button
              type="button"
              class="tw:font-medium tw:text-primary tw:hover:underline tw:disabled:cursor-not-allowed tw:disabled:text-muted"
              :disabled="isCalling || isSendingCode"
              data-testid="reg-mobile-call"
              @click="requestVoice">
              {{ isCalling ? $t('registration.profile.mobile_calling') : $t('registration.profile.mobile_call') }}
            </button>
          </template>
          <!-- during the cooldown, only the countdown is shown -->
          <ResendCountdown
            v-else-if="cooldownActive"
            ref="countdownRef"
            :seconds="resendAfterSeconds"
            :busy="isSendingCode"
            @resend="() => sendCode()"
            @finished="cooldownActive = false" />
          <!-- after the cooldown, the message and resend link return -->
          <template v-else>
            {{ $t('registration.profile.mobile_didnt_get') }}
            <button
              type="button"
              class="tw:font-medium tw:text-primary tw:hover:underline tw:disabled:cursor-not-allowed tw:disabled:text-muted"
              :disabled="isSendingCode"
              data-testid="reg-mobile-resend"
              @click="() => sendCode()">
              {{ $t('registration.profile.mobile_resend') }}
            </button>
            {{ ' ' }}{{ $t('registration.profile.mobile_or') }}{{ ' ' }}
            <button
              type="button"
              class="tw:font-medium tw:text-primary tw:hover:underline tw:disabled:cursor-not-allowed tw:disabled:text-muted"
              :disabled="isCalling || isSendingCode"
              data-testid="reg-mobile-call"
              @click="requestVoice">
              {{ isCalling ? $t('registration.profile.mobile_calling') : $t('registration.profile.mobile_call') }}
            </button>
          </template>
        </span>
      </template>
    </div>
  </section>
</template>

<script setup lang="ts">
/** Step 3 - the name and the mobile confirmation are both required to continue. Language is the header's selector, so there is no field for it */
import { IconBriefcase, IconCheck } from '@tabler/icons-vue';
import { computed, nextTick, onMounted, reactive, ref, useTemplateRef, watch } from 'vue';

import { registrationClient } from '@/api/registration';
import CodeInput from '@/components/registration/subcomponents/CodeInput.vue';
import RegistrationStepHeading from '@/components/registration/subcomponents/RegistrationStepHeading.vue';
import ResendCountdown from '@/components/registration/subcomponents/ResendCountdown.vue';
import {
  readRegistrationErrorCode,
  registrationErrorMessage,
  verificationErrorMessage,
} from '@/components/registration/subcomponents/registrationErrors';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import LeadInput from '@/components/lead-design/LeadInput.vue';
import LeadPill from '@/components/generic/LeadPill.vue';
import LanguageSelect from '@/components/generic/LanguageSelect.vue';
import { useRegistrationStepSync } from '@/composables/useRegistrationStepSync';
import translator from '@/locale/translator';
import { useRegistrationStore } from '@/stores/registration';
import { useUserStore } from '@/stores/user';
import type { RegistrationErrorCode } from '@/types/registration';

const CODE_LENGTH = 6;

/* ----------------------------------- STATE ---------------------------------- */
const allowNext = defineModel<boolean>('allowNext', { required: true });

const store = useRegistrationStore();
const userStore = useUserStore();

const changingNumber = ref(false);
const correctedMobile = ref('');
const code = ref('');
const codeSent = ref(false);
/** true while the resend cooldown runs */
const cooldownActive = ref(false);
const error = ref('');
const isSendingCode = ref(false);
const isVerifying = ref(false);
const isCalling = ref(false);
const resendAfterSeconds = ref(0);
const maskedMobile = ref('');
const fieldErrors = reactive<{ firstName: string; lastName: string; mobile: string }>({
  firstName: '',
  lastName: '',
  mobile: '',
});

const codeRef = useTemplateRef<InstanceType<typeof CodeInput>>('codeRef');
const countdownRef = useTemplateRef<InstanceType<typeof ResendCountdown>>('countdownRef');

/* --------------------------------- COMPUTED --------------------------------- */
// bound to the draft so a reload recovers them
const firstName = computed({
  get: () => store.draft.profile.firstName,
  set: (value: string) => {
    store.draft.profile.firstName = value;
  },
});

const lastName = computed({
  get: () => store.draft.profile.lastName,
  set: (value: string) => {
    store.draft.profile.lastName = value;
  },
});

const jobTitle = computed({
  get: () => store.draft.profile.jobTitle,
  set: (value: string) => {
    store.draft.profile.jobTitle = value;
  },
});

const mobileVerified = computed(() => store.mobileVerified);

/** masked number to show; the backend owns the mask, with the step 1 mask as fallback for the mock flow */
const displayNumber = computed(() => {
  if (maskedMobile.value) return maskedMobile.value;

  return userStore.currentUser?.phone_number ?? store.verification.maskedMobile ?? '';
});

const isFormValid = computed(
  () => firstName.value.trim() !== '' && lastName.value.trim() !== '' && mobileVerified.value
);

/* --------------------------------- METHODS ---------------------------------- */
function clearErrors() {
  error.value = '';
  fieldErrors.firstName = '';
  fieldErrors.lastName = '';
  fieldErrors.mobile = '';
}

function messageFor(code: RegistrationErrorCode | undefined) {
  return verificationErrorMessage(code, 'mobile');
}

async function sendCode(mobile?: string) {
  if (isSendingCode.value) return;

  isSendingCode.value = true;
  error.value = '';
  fieldErrors.mobile = '';

  try {
    const response = await registrationClient.resendSmsCode(mobile ? { mobile } : {});
    maskedMobile.value = response.maskedMobile ?? maskedMobile.value;
    resendAfterSeconds.value = response.resendAfterSeconds;
    codeSent.value = true;
    cooldownActive.value = true;
    code.value = '';
    // on first send the countdown isn't mounted yet; its mount watcher picks up the seconds
    nextTick(() => countdownRef.value?.restart(response.resendAfterSeconds));
  } catch (caught) {
    const reason = readRegistrationErrorCode(caught);
    // a number that cannot be used is a field error; a code already in flight isn't a failure, so the countdown just restarts
    if (reason === 'verification_invalid_phone' || reason === 'verification_delivery_unavailable') {
      showMobileFieldError(caught);
    } else if (reason === 'verification_throttled') {
      codeSent.value = true;
      cooldownActive.value = true;
      resendAfterSeconds.value = 60;
      nextTick(() => countdownRef.value?.restart(60));
    } else {
      error.value = registrationErrorMessage(caught, messageFor);
    }
  } finally {
    isSendingCode.value = false;
  }
}

async function verify() {
  if (code.value.length !== CODE_LENGTH || isVerifying.value) return;

  isVerifying.value = true;
  error.value = '';

  try {
    const response = await registrationClient.verifyMobile({ code: code.value });
    if (!response.mobileVerified) {
      error.value = messageFor('verification_code_incorrect');
      code.value = '';
      return;
    }
  } catch (caught) {
    error.value = registrationErrorMessage(caught, messageFor);
    code.value = '';
    return;
  } finally {
    isVerifying.value = false;
  }

  // the durable record is on the backend; this flag keeps the UI in step and survives the language reload
  store.mobileVerified = true;
  if (userStore.currentUser) userStore.currentUser.phone_verified_at = new Date().toISOString();
  code.value = '';
}

async function requestVoice() {
  if (isCalling.value || isSendingCode.value) return;

  isCalling.value = true;
  error.value = '';

  try {
    const response = await registrationClient.requestVoiceCode();
    resendAfterSeconds.value = response.resendAfterSeconds;
    codeSent.value = true;
    cooldownActive.value = true;
    code.value = '';
    nextTick(() => countdownRef.value?.restart(response.resendAfterSeconds));
  } catch (caught) {
    if (readRegistrationErrorCode(caught) === 'verification_delivery_unavailable') {
      showMobileFieldError(caught);
    } else {
      error.value = registrationErrorMessage(caught, messageFor);
    }
  } finally {
    isCalling.value = false;
  }
}

/** puts the error on the number field, opening it first so the registrant can enter another number */
function showMobileFieldError(caught: unknown) {
  if (!changingNumber.value) startChangingNumber();
  fieldErrors.mobile = registrationErrorMessage(caught, messageFor);
}

function startChangingNumber() {
  changingNumber.value = true;
  // the session's phone_number is masked, so edit the draft's raw input
  correctedMobile.value = store.draft.account.mobile;
}

function cancelChange() {
  changingNumber.value = false;
  correctedMobile.value = '';
  fieldErrors.mobile = '';
}

/** updating the number sends the code to it, which also records the change */
async function saveNumber() {
  const next = correctedMobile.value.trim();
  if (!next) {
    fieldErrors.mobile = messageFor('verification_invalid_phone');
    return;
  }

  await sendCode(next);
  if (!fieldErrors.mobile) {
    changingNumber.value = false;
    // a changed number was never confirmed
    store.mobileVerified = false;
    if (userStore.currentUser) userStore.currentUser.phone_verified_at = null;
  }
}

async function submit(): Promise<boolean> {
  clearErrors();

  if (!firstName.value.trim()) fieldErrors.firstName = translator.get('registration.profile.name_required');
  if (!lastName.value.trim()) fieldErrors.lastName = translator.get('registration.profile.name_required');
  if (!mobileVerified.value) {
    error.value = translator.get('registration.profile.mobile_required');
    return false;
  }
  if (!firstName.value.trim() || !lastName.value.trim()) return false;

  // language is not sent: the platform persists it on every request
  await registrationClient.submitProfile({
    firstName: firstName.value.trim(),
    lastName: lastName.value.trim(),
    jobTitle: jobTitle.value.trim(),
  });

  return true;
}

// pre-fills the draft from the backend on every entry; `isFormValid` already watches the draft
useRegistrationStepSync('profile');

/* -------------------------------- LIFECYCLE --------------------------------- */
onMounted(() => {
  store.availableSteps.profile.onContinue = submit;
  allowNext.value = isFormValid.value;
  // the code is not requested on load: an SMS is the user's to trigger
});

/* --------------------------------- WATCHERS --------------------------------- */
watch(isFormValid, (valid) => {
  allowNext.value = valid;
});

watch(code, (value) => {
  // clear as soon as the registrant starts correcting
  if (error.value && value.length > 0) error.value = '';
});
</script>
